import { supabase } from '@/integrations/supabase/client';

export interface UserProfile {
  id: string;
  display_name: string;
  email: string;
  company_id: string | null;
  company_name: string | null;
  auth_user_id: string | null;
}

export interface UserRole {
  role: 'admin' | 'company_admin' | 'user' | 'editor' | 'sales_rep';
}

export interface AgentVisibility {
  id: string;
  name: string;
  type: string;
  platform: string;
  company_id: string | null;
  company_name: string | null;
  assign_to: string | null;
  status: string | null;
  isVisible: boolean;
  visibilityReason: string;
}

export interface UserVisibilityReport {
  user: UserProfile;
  roles: string[];
  agents: AgentVisibility[];
  visibleCount: number;
  hiddenCount: number;
}

// Fetch all users for selection dropdown
export async function fetchAllUsersForDebugger(): Promise<UserProfile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, email, company_id, company_name, auth_user_id')
    .order('display_name');

  if (error) {
    console.error('Error fetching users:', error);
    throw error;
  }

  return data || [];
}

// Fetch user roles
export async function fetchUserRoles(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching user roles:', error);
    return ['user']; // Default to user role
  }

  return data?.map(r => r.role) || ['user'];
}

// Fetch agent assignments for a user
export async function fetchUserAgentAssignments(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('agent_assignments')
    .select('agent_id')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching agent assignments:', error);
    return [];
  }

  return data?.map(a => a.agent_id) || [];
}

// Fetch all agents (admin view - bypasses RLS)
export async function fetchAllAgentsForDebugger(): Promise<any[]> {
  const { data, error } = await supabase
    .from('simulation_agents')
    .select(`
      id,
      name,
      type,
      platform,
      company_id,
      assign_to,
      status,
      companies:company_id (name)
    `)
    .order('name');

  if (error) {
    console.error('Error fetching agents:', error);
    throw error;
  }

  return data || [];
}

// Main function: Calculate what agents a user would see
export async function fetchUserAgentVisibility(userId: string): Promise<UserVisibilityReport> {
  // Fetch user profile
  const { data: userData, error: userError } = await supabase
    .from('profiles')
    .select('id, display_name, email, company_id, company_name, auth_user_id')
    .eq('id', userId)
    .single();

  if (userError || !userData) {
    throw new Error('User not found');
  }

  // Fetch user roles
  const roles = await fetchUserRoles(userId);
  const isAdmin = roles.includes('admin');

  // Fetch all agents
  const agents = await fetchAllAgentsForDebugger();

  // Fetch user's specific agent assignments
  const assignedAgentIds = await fetchUserAgentAssignments(userId);

  // Calculate visibility for each agent using NEW logic:
  // - If agent has company_id: only visible to that company's users OR specifically assigned users
  // - If agent has no company_id: check assign_to field (all = everyone, specific = only assigned)
  const agentVisibility: AgentVisibility[] = agents.map(agent => {
    let isVisible = false;
    let visibilityReason = '';

    // Super admin sees everything
    if (isAdmin) {
      isVisible = true;
      visibilityReason = '🔑 Admin access (sees all)';
    }
    // Check if agent is active
    else if (agent.status !== 'active' && agent.status !== null) {
      isVisible = false;
      visibilityReason = `❌ Agent inactive (status: ${agent.status})`;
    }
    // Agent HAS a company_id - company-restricted visibility
    else if (agent.company_id) {
      // User is from same company
      if (userData.company_id && agent.company_id === userData.company_id) {
        isVisible = true;
        visibilityReason = '🏢 Company agent (user is in same company)';
      }
      // User is specifically assigned to this agent
      else if (assignedAgentIds.includes(agent.id)) {
        isVisible = true;
        visibilityReason = '👤 Specifically assigned (cross-company)';
      }
      // No access - different company and not assigned
      else {
        isVisible = false;
        visibilityReason = `❌ Company-restricted (${agent.companies?.name || 'Unknown'})`;
      }
    }
    // Agent has NO company_id - check assign_to field
    else {
      // Global agent (assign_to = 'all' or null)
      if (agent.assign_to === 'all' || !agent.assign_to) {
        isVisible = true;
        visibilityReason = '🌐 Global agent (no company, visible to all)';
      }
      // Specific assignment only
      else if (assignedAgentIds.includes(agent.id)) {
        isVisible = true;
        visibilityReason = '👤 Specifically assigned to user';
      }
      // No access
      else {
        isVisible = false;
        visibilityReason = '❌ Specific assignment only (not assigned)';
      }
    }

    return {
      id: agent.id,
      name: agent.name,
      type: agent.type,
      platform: agent.platform,
      company_id: agent.company_id,
      company_name: agent.companies?.name || null,
      assign_to: agent.assign_to,
      status: agent.status,
      isVisible,
      visibilityReason,
    };
  });

  const visibleCount = agentVisibility.filter(a => a.isVisible).length;
  const hiddenCount = agentVisibility.filter(a => !a.isVisible).length;

  return {
    user: userData,
    roles,
    agents: agentVisibility,
    visibleCount,
    hiddenCount,
  };
}
